import React, { useState, useMemo } from "react";
import { Search, ChevronDown, Briefcase, Award, CreditCard, MessageSquare, HelpCircle } from "lucide-react";

const faqs = [
  {
    category: "internship",
    question: "How do I apply for an internship?",
    answer: "First, click 'Register' in the navigation bar to create your student account. Once registered, log in to access your dashboard. Complete your profile by filling out your education, skills, and resume details. Then browse the 'Internships' page and click 'Apply Now' on the domain that fits your career goals.",
  },
  {
    category: "fees",
    question: "Is there any fee for the internships?",
    answer: "No, all internships and training programs provided by Student Era are 100% free of charge. We believe in providing accessible quality education and real-world project experience to all students without financial barriers.",
  },
  {
    category: "verification",
    question: "How do I verify my certificate?",
    answer: "Go to the 'Verify Certificate' page from the navigation bar. Enter the unique Certificate ID listed on your digital certificate and click 'Verify'. The system will instantly fetch and validate your internship details directly from our database.",
  },
  {
    category: "internship",
    question: "What is the duration of the internships?",
    answer: "Our standard internship programs last for 4 weeks (1 month). However, depending on the complexity of the domain (e.g., Full Stack Development), some advanced tracks may extend up to 8 weeks to ensure deep project work.",
  },
  {
    category: "internship",
    question: "Will I receive mentorship during my internship?",
    answer: "Yes, you will be assigned to a cohort and have access to our technical mentors. You can ask questions, get code reviews, and discuss issues in our community support channels.",
  },
  {
    category: "verification",
    question: "Are the certificates shareable on LinkedIn?",
    answer: "Yes, all our certificates are issued with a unique, permanent verification link. You can add this URL directly to your LinkedIn licenses and certifications section so recruiters can verify your credentials with a single click.",
  },
  {
    category: "fees",
    question: "Are there any hidden costs for certification?",
    answer: "Absolutely not. The certificate generation, verification, and digital issue are entirely free. We do not charge anything for certificates upon completion of your internship tasks.",
  },
  {
    category: "support",
    question: "How do I contact support if I face issues?",
    answer: "You can contact our support team by emailing us at contact@studentera.online or via the contact info in the footer. We typically reply within 24 hours to help resolve any technical or administrative queries.",
  },
  {
    category: "support",
    question: "What should I do if my task submission is delayed?",
    answer: "If you need an extension due to exams or health reasons, please reach out to your mentor or submit a support request. We are student-friendly and happy to accommodate reasonable extension requests.",
  }
];

const categoryMap = {
  all: { label: "All Questions", icon: HelpCircle },
  internship: { label: "Internships", icon: Briefcase },
  verification: { label: "Verification", icon: Award },
  fees: { label: "Fees & Payments", icon: CreditCard },
  support: { label: "Support & Contact", icon: MessageSquare }
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesSearch = 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const toggle = idx => setOpenIndex(openIndex === idx ? null : idx);

  // Helper to dynamically match icons
  const getCategoryIcon = (category) => {
    switch (category) {
      case "internship": return Briefcase;
      case "verification": return Award;
      case "fees": return CreditCard;
      case "support": return MessageSquare;
      default: return HelpCircle;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 font-sans">
      {/* Background Decorative Blobs */}
      <div className="absolute top-1/4 -left-32 w-72 sm:w-96 h-72 sm:h-96 bg-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-32 w-72 sm:w-96 h-72 sm:h-96 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse delay-1000 pointer-events-none"></div>

      <div className="relative max-w-4xl mx-auto z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-center mb-4 text-gradient-premium">
            Frequently Asked Questions
          </h1>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto font-medium">
            Find answers to common questions about our internship programs, certificates, and support systems.
          </p>
        </div>

        {/* Search & Category Filter Box (Glassmorphic) */}
        <div className="glass-card rounded-3xl p-5 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 mb-8 sm:mb-10">
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-white/80 border border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 text-slate-800 placeholder-slate-400 transition-all duration-300 shadow-inner text-base"
              aria-label="Search FAQs"
            />
          </div>

          <div className="flex flex-wrap gap-2 sm:gap-2.5">
            {Object.keys(categoryMap).map((cat) => {
              const CatIcon = categoryMap[cat].icon;
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setOpenIndex(null); // Reset open accordion on category change
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 active:scale-95 ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "bg-white/80 border border-slate-200/60 hover:bg-slate-50 text-slate-600 hover:text-slate-800"
                  }`}
                >
                  <CatIcon className="w-4 h-4 shrink-0" />
                  <span>{categoryMap[cat].label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Accordion Lists */}
        <div className="space-y-4">
          {filteredFaqs.map((faq, idx) => {
            const IconComponent = getCategoryIcon(faq.category);
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`glass-card rounded-2xl border transition-all duration-300 ${
                  isOpen
                    ? "border-indigo-300/85 shadow-md shadow-indigo-100/30 ring-1 ring-indigo-200/50"
                    : "border-slate-200/60 shadow-sm hover:border-indigo-300/40 hover:shadow-md hover:shadow-slate-100"
                }`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full text-left px-5 sm:px-6 py-4.5 flex items-center justify-between gap-4 select-none"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-2.5 rounded-xl transition-colors duration-300 ${
                        isOpen ? "bg-indigo-50 text-indigo-600" : "bg-slate-50 text-slate-500"
                      }`}
                    >
                      <IconComponent className="w-5 h-5 shrink-0" />
                    </div>
                    <span className="font-bold text-slate-800 text-base sm:text-lg tracking-tight">
                      {faq.question}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180 text-indigo-500" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div
                    id={`faq-answer-${idx}`}
                    className="px-5 sm:px-6 pb-5 pt-1 border-t border-slate-100 animate-[fadeIn_0.2s_ease-out]"
                  >
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed pl-[48px]">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredFaqs.length === 0 && (
          <div className="text-center py-16 bg-white/50 border border-dashed border-slate-200 rounded-3xl backdrop-blur-sm shadow-sm animate-fadeIn">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg font-bold">No FAQs found matching your criteria.</p>
            <p className="text-slate-400 text-sm mt-1">Try resetting the search or category filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQ;
